
import Vue, { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { split, head, last, pipe, lowerCase } from 'lodash/fp'

import { UITechpackAttachment, UITechpackModel } from '../store/techpack/techpack.types'
import vuetify from '../plugins/vuetify'
import AdobePDFViewer from './AdobePDFViewer.vue'

const getFileExtension = pipe(split('?'), head, split('.'), last, lowerCase)

export default defineComponent({
  name: 'InlineTechpack',
  vuetify,
  components: {
    AdobePDFViewer
  },
  props: {
    techpack: {
      type: Object as () => UITechpackModel,
      required: true
    },
    previewFiles: {
      type: Array as () => Array<UITechpackAttachment>,
      required: true,
      default: []
    },
    nonPreviewFiles: {
      type: Array as () => Array<UITechpackAttachment>,
      required: true,
      default: []
    },
    adobeClientId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const techpackHasAttachments = computed(() => {
      return !!props.techpack?.attachments?.length ?? false
    })

    const drawer = ref(!!techpackHasAttachments.value)
    const showPDF = ref(true)
    const selectedAttachment = ref({})
    const downloadingAll = ref(false)
    const pdfUrl = ref('')
    const fileName = ref('')

    watch(
      () => props.techpack,
      (techpack, oldTechpack) => {
        if (oldTechpack && techpack._id !== oldTechpack?._id) {
          previewPDF(props.techpack.link)
        }
      }
    )

    onMounted(() => {
      Vue.nextTick(() => {
        previewPDF(props.techpack.link)
      })
    })

    async function download(url: string, fileName: string) {
      const response = await fetch(url)
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      link.click()
      URL.revokeObjectURL(link.href)
    }

    async function downloadAll() {
      downloadingAll.value = true
      const downloadTP = download(props.techpack.link, props.techpack?.product?.name || '')
      const downloadAttachments = props.techpack?.attachments?.map((att: any) =>
        download(att.link || '', att.name || '')
      )
      await Promise.all([downloadTP, downloadAttachments])
      downloadingAll.value = false
    }

    function previewPDF(url: string) {
      selectedAttachment.value = {}
      showPDF.value = true
      const fileParts = url.split('/')
      const fileNameParts = fileParts[fileParts.length - 1].split('?')
      const fileNameString = fileNameParts[0]
      fileName.value = fileNameString
      pdfUrl.value = url
    }

    async function previewFile(attachment: any) {
      const fileExtension = getFileExtension(attachment.link)
      if (fileExtension !== 'pdf') {
        showPDF.value = false
        selectedAttachment.value = attachment
      } else {
        previewPDF(attachment.link || '')
      }
    }

    function navigateBack(): void {
      window.history.back()
    }

    return {
      pdfUrl,
      showPDF,
      fileName,
      drawer,
      previewFile,
      downloadAll,
      downloadingAll,
      navigateBack,
      selectedAttachment,
      techpackHasAttachments
    }
  }
})
